import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { createPublisher } from '../../../api/marketplaceApi';
import type { Publisher } from 'types/publisher';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import IconPickerAtom from '../../atoms/IconPicker/IconPickerAtom';
import { hueFromSlug } from '../../../utils/hueFromSlug';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './PublisherCreateDialogOrganism.scss';

export interface PublisherCreateDialogOrganismProps {
  open: boolean;
  onClose: () => void;
  onCreated: (publisher: Publisher) => void;
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .substring(0, 40);
}

const PublisherCreateDialogOrganism: React.FC<PublisherCreateDialogOrganismProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [bio, setBio] = useState('');
  const [glyphKind, setGlyphKind] = useState<'letter' | 'icon'>('letter');
  const [glyphIcon, setGlyphIcon] = useState('lucide:circle');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slugTouched) setSlug(deriveSlug(displayName));
  }, [displayName, slugTouched]);

  const resetForm = useCallback(() => {
    setDisplayName('');
    setSlug('');
    setSlugTouched(false);
    setBio('');
    setGlyphKind('letter');
    setGlyphIcon('lucide:circle');
    setError(null);
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) handleClose();
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  const slugValid = !!slug && SLUG_RE.test(slug) && slug.length <= 40;
  const canSubmit = displayName.trim().length > 0 && slugValid;
  const previewHue = slug ? hueFromSlug(slug) : 210;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit || !apiClients || submitting) return;

      setSubmitting(true);
      setError(null);

      try {
        const result = await createPublisher(apiClients.schemaClient, {
          displayName: displayName.trim(),
          slug: slug.trim(),
          bio: bio.trim() || undefined,
          hue: previewHue,
          glyphKind,
          // Letter mode lets the server default the glyph to the brand initial.
          glyph: glyphKind === 'icon' ? glyphIcon : undefined,
        });
        resetForm();
        onCreated(result.publisher);
      } catch (err) {
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosErr = err as { response?: { status?: number } };
          if (axiosErr.response?.status === 409) {
            setError(t('marketplace.publishers.create.errorConflict'));
          } else if (axiosErr.response?.status === 400) {
            setError(t('marketplace.publishers.create.errorInvalid'));
          } else {
            setError(t('marketplace.publishers.create.errorGeneric'));
          }
        } else {
          setError(t('marketplace.publishers.create.errorGeneric'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, apiClients, submitting, displayName, slug, bio, previewHue, glyphKind, glyphIcon, resetForm, onCreated, t]
  );

  if (!open) return null;

  const hue2 = (previewHue + 30) % 360;
  const headerBg = `linear-gradient(135deg, hsl(${previewHue} 55% 22%), hsl(${hue2} 60% 14%))`;

  return (
    <div
      className="marketplace-dialog-overlay marketplace-publisher-create-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        className="marketplace-publisher-create-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-publisher-title"
      >
        {/* Gradient header — live branding preview */}
        <div
          className="marketplace-publisher-create-dialog__header"
          style={{ background: headerBg }}
        >
          <ScopeCoinAtom
            scope="metasphere"
            glyph={glyphKind === 'icon' ? glyphIcon : displayName ? displayName.charAt(0).toUpperCase() : undefined}
            channelName={displayName || t('marketplace.publishers.create.namePlaceholderHeader')}
            glyphKind={glyphKind}
            hue={previewHue}
            size="lg"
          />
          <div className="marketplace-publisher-create-dialog__header-meta">
            <h2
              id="create-publisher-title"
              className="marketplace-publisher-create-dialog__header-name"
            >
              {displayName || t('marketplace.publishers.create.namePlaceholderHeader')}
            </h2>
            <span className="marketplace-publisher-create-dialog__header-url">
              /marketplace/publishers/{slug || t('marketplace.publishers.create.slugPlaceholderHeader')}
            </span>
          </div>
          <button
            type="button"
            className="marketplace-publisher-create-dialog__header-close"
            aria-label={t('marketplace.publishers.create.closeLabel')}
            onClick={handleClose}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="marketplace-publisher-create-dialog__body">
            {/* Display name */}
            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label" htmlFor="publisher-display-name">
                {t('marketplace.publishers.create.displayNameLabel')}
              </label>
              <input
                id="publisher-display-name"
                type="text"
                className="marketplace-dialog__input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('marketplace.publishers.create.displayNamePlaceholder')}
                maxLength={80}
                disabled={submitting}
                autoFocus
              />
              <span className="marketplace-dialog__field-hint">
                {t('marketplace.publishers.create.displayNameHint')}
              </span>
            </div>

            {/* Slug with prefix decoration */}
            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label" htmlFor="publisher-slug">
                {t('marketplace.publishers.create.slugLabel')}
              </label>
              <div className="marketplace-publisher-create-dialog__slug-row">
                <span
                  className="marketplace-publisher-create-dialog__slug-prefix"
                  aria-hidden="true"
                >
                  {t('marketplace.publishers.create.slugPrefix')}
                </span>
                <input
                  id="publisher-slug"
                  type="text"
                  className="marketplace-publisher-create-dialog__slug-input"
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value.toLowerCase());
                  }}
                  maxLength={40}
                  disabled={submitting}
                />
              </div>
              {slug && !slugValid && (
                <span
                  className="marketplace-publisher-create-dialog__slug-error"
                  role="alert"
                >
                  <i className="pi pi-exclamation-circle" aria-hidden="true" />
                  {t('marketplace.publishers.create.slugValidationError')}
                </span>
              )}
              <span className="marketplace-dialog__field-hint">
                {t('marketplace.publishers.create.slugHint')}
              </span>
            </div>

            {/* Bio */}
            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label" htmlFor="publisher-bio">
                {t('marketplace.publishers.create.bioLabel')}
              </label>
              <textarea
                id="publisher-bio"
                className="marketplace-dialog__textarea"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder={t('marketplace.publishers.create.bioPlaceholder')}
                maxLength={500}
                rows={3}
                disabled={submitting}
              />
              <span className="marketplace-dialog__field-hint">
                {t('marketplace.publishers.create.bioHint')}
              </span>
            </div>

            {/* Glyph: brand letter or Iconify icon */}
            <div className="marketplace-dialog__field">
              <span className="marketplace-dialog__label">
                {t('marketplace.publishers.manage.branding.glyphKindLabel')}
              </span>
              <div className="marketplace-publisher-create-dialog__glyph-kind" role="group">
                <ButtonAtom
                  variant={glyphKind === 'letter' ? 'primary' : 'secondary'}
                  shape="pill"
                  type="button"
                  label={t('marketplace.publishers.manage.branding.glyphKindLetter')}
                  onClick={() => setGlyphKind('letter')}
                  disabled={submitting}
                />
                <ButtonAtom
                  variant={glyphKind === 'icon' ? 'primary' : 'secondary'}
                  shape="pill"
                  type="button"
                  label={t('marketplace.publishers.manage.branding.glyphKindIcon')}
                  onClick={() => setGlyphKind('icon')}
                  disabled={submitting}
                />
              </div>
              {glyphKind === 'icon' && (
                <div className="marketplace-publisher-create-dialog__glyph-picker">
                  <IconPickerAtom
                    value={glyphIcon}
                    onChange={setGlyphIcon}
                    tooltip={t('marketplace.publishers.manage.branding.glyphIconHint')}
                  />
                </div>
              )}
              <span className="marketplace-dialog__field-hint">
                {glyphKind === 'icon'
                  ? t('marketplace.publishers.manage.branding.glyphIconHint')
                  : t('marketplace.publishers.manage.branding.glyphHint')}
              </span>
            </div>

            {/* Workflow info card */}
            <div className="marketplace-publisher-create-dialog__info-card" role="note">
              <i className="pi pi-info-circle marketplace-publisher-create-dialog__info-icon" aria-hidden="true" />
              <span>
                {t('marketplace.publishers.create.workflowInfoBefore')}
                <b>{t('marketplace.publishers.create.workflowInfoDraft')}</b>
                {t('marketplace.publishers.create.workflowInfoAfter')}
              </span>
            </div>

            {error && (
              <div className="marketplace-dialog__error" role="alert">{error}</div>
            )}
          </div>

          <div className="marketplace-publisher-create-dialog__footer">
            <ButtonAtom
              variant="secondary"
              shape="pill"
              type="button"
              label={t('marketplace.publishers.create.cancelButton')}
              onClick={handleClose}
              disabled={submitting}
            />
            <ButtonAtom
              variant="primary"
              shape="pill"
              type="submit"
              icon={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
              iconPos="left"
              label={
                submitting
                  ? t('marketplace.publishers.create.creating')
                  : t('marketplace.publishers.create.createButton')
              }
              disabled={!canSubmit || submitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export { PublisherCreateDialogOrganism };
export default PublisherCreateDialogOrganism;
